<template>
  <div>
    <div class="d-flex mb-3">
      <button class="btn btn-green d-flex align-items-center" @click="openRegisterInboundDelivery">
        <mdicon name="TruckDeliveryOutline" class="me-2" />
        Register inbound delivery
      </button>
    </div>

    <EasyDataTable
      ref="dataTable"
      v-model:server-options="serverOptions"
      table-class-name="vue3-easy-data-table table-shadow no-overflow"
      :headers="headers"
      :items="receipts"
      :server-items-length="serverItemsLength"
      :loading="loading"
      :body-item-class-name="bodyItemClassName"
      hide-footer
      no-hover
      alternating
      @update:server-options="updateServerOptions"
    >
      <template #item="{ column, item }">
        <span v-if="item">
          <span v-if="column === 'receivedAt'">
            <RowDate :date="item.receivedAt" />
          </span>
          <span v-else-if="column === 'description'">{{ item.description || '-' }}</span>
          <span v-else-if="column === 'lines'">
            <InboundReceiptLinesTable :lines="item.lines" />
          </span>
          <span v-else>{{ item[column] }}</span>
        </span>
      </template>
      <template #no-data>-</template>
    </EasyDataTable>

    <TablePagination
      v-model:server-options-ref="serverOptions"
      v-model:data-table-ref="dataTable"
      :server-items-length="serverItemsLength"
    />

    <RegisterInboundDeliveryModal
      :id="REGISTER_INVENTORY_INBOUND_DELIVERY"
      :company-id="companyId"
      @created="handleReceiptCreated"
    />
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, ref } from 'vue'
import { useInventoryServerPagination } from '@/composables/useInventoryServerPagination'
import { REGISTER_INVENTORY_INBOUND_DELIVERY } from '@/const/ModalTypes'
import { openModal } from '@/services/Modal'
import { useInventoryStore } from '@/store/inventory'
import EasyDataTable, { type Header } from 'vue3-easy-data-table'
import RowDate from '@/views/Components/Elements/Table/RowDate.vue'
import TablePagination from '@/views/Components/Elements/Table/TablePagination.vue'
import InboundReceiptLinesTable from '@/views/Staff/Inventory/InboundReceiptLinesTable.vue'
import RegisterInboundDeliveryModal from '@/views/Modals/Inventory/RegisterInboundDeliveryModal.vue'

export default defineComponent({
  name: 'CreateCustomerPage',
  components: { EasyDataTable, RowDate, TablePagination, RegisterInboundDeliveryModal, InboundReceiptLinesTable },
  props: {
    companyId: {
      type: String,
      required: true,
    },
  },
  setup(props) {
    const inventoryStore = useInventoryStore()
    const receipts = computed(() => inventoryStore.receiptsList)
    const loading = computed(() => inventoryStore.inboundReceiptsLoading)
    const serverItemsLength = computed(() => inventoryStore.inboundReceiptsCount)
    const dataTable = ref()
    const companyId = computed(() => props.companyId)

    const headers: Header[] = [
      { text: 'Arrival date', value: 'receivedAt' },
      { text: 'Description', value: 'description' },
      { text: 'Products', value: 'lines' },
    ]

    const bodyItemClassName = (column: string) => (column === 'lines' ? 'inventory-lines-cell' : '')

    const { serverOptions, updateServerOptions, resetAndReload } = useInventoryServerPagination(companyId, (options) =>
      inventoryStore.fetchInboundReceipts(companyId.value, { page: options.page, rowsPerPage: options.rowsPerPage }),
    )

    const openRegisterInboundDelivery = () => openModal(REGISTER_INVENTORY_INBOUND_DELIVERY)

    const handleReceiptCreated = () => {
      resetAndReload()
    }

    return {
      headers,
      loading,
      receipts,
      dataTable,
      serverOptions,
      serverItemsLength,
      bodyItemClassName,
      updateServerOptions,
      handleReceiptCreated,
      openRegisterInboundDelivery,

      REGISTER_INVENTORY_INBOUND_DELIVERY,
    }
  },
})
</script>

<style scoped>
.inventory-lines-cell {
  padding-top: 20px !important;
  padding-bottom: 20px !important;
  vertical-align: top;
}
</style>
